import { memo, useRef, useState, type FormEvent, type KeyboardEvent } from 'react'
import { motion } from 'motion/react'
import { Check, Copy, Pencil } from 'lucide-react'
import { useAutosize } from '@/hooks/useAutosize'
import { useCopy } from '@/hooks/useCopy'
import { chatController } from '@/stores/chatController'
import { useUiStore } from '@/stores/uiStore'
import { spring } from '@/lib/motion'
import { Button } from '@/components/ui/Button'
import { IconButton } from '@/components/ui/IconButton'

type Props = {
  /** Server id, or null until the backend acknowledges the message. */
  messageId: string | null
  content: string
  conversationId: string | null
  /** Editing is allowed for stored messages when nothing is streaming. */
  canEdit: boolean
  onEdited?: () => void
  /** Spring in from the composer (messages sent in this session). */
  animateIn?: boolean
}

export const UserMessage = memo(function UserMessage({
  messageId,
  content,
  conversationId,
  canEdit,
  onEdited,
  animateIn = false,
}: Props) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(content)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const { copied, copy } = useCopy()
  useAutosize(textareaRef, draft, 320)

  const startEdit = () => {
    setDraft(content)
    setEditing(true)
  }

  const save = (event?: FormEvent) => {
    event?.preventDefault()
    const text = draft.trim()
    if (!text || !conversationId || !messageId) return
    if (text === content.trim()) {
      setEditing(false)
      return
    }
    const ok = chatController.edit(conversationId, messageId, text, useUiStore.getState().department)
    if (ok) {
      setEditing(false)
      onEdited?.()
    }
  }

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      event.stopPropagation()
      setEditing(false)
    } else if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      save()
      event.preventDefault()
    }
  }

  if (editing) {
    return (
      <motion.form
        onSubmit={save}
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={spring.snappy}
        className="ml-auto w-full max-w-[85%] rounded-[22px] bg-surface p-3.5 shadow-lift"
      >
        <label className="sr-only" htmlFor={`edit-${messageId}`}>
          Edit your message
        </label>
        <textarea
          id={`edit-${messageId}`}
          ref={textareaRef}
          value={draft}
          autoFocus
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          rows={1}
          className="block w-full resize-none bg-transparent text-[15px] leading-7 text-ink focus:outline-none"
        />
        <p className="mt-1 text-xs text-ink-faint">Saving resends this question and replaces the messages after it.</p>
        <div className="mt-2 flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>
            Cancel
          </Button>
          <Button size="sm" type="submit" disabled={!draft.trim()}>
            Save &amp; send
          </Button>
        </div>
      </motion.form>
    )
  }

  return (
    <div className="group/user flex flex-col items-end">
      <motion.div
        initial={animateIn ? { opacity: 0, y: 24, scale: 0.94 } : false}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={spring.smooth}
        style={{ transformOrigin: '100% 100%' }}
        className="max-w-[85%] whitespace-pre-wrap break-words rounded-[22px] rounded-br-[8px] bg-bubble px-4 py-2.5 text-[15.5px] leading-[1.6] text-bubble-ink shadow-[0_1px_2px_rgb(0_0_0/0.08)]"
      >
        {content}
      </motion.div>
      <div className="mt-1 flex gap-0.5 transition-opacity duration-300 md:opacity-0 md:focus-within:opacity-100 md:group-hover/user:opacity-100">
        <IconButton label={copied ? 'Copied' : 'Copy message'} size="xs" onClick={() => void copy(content)}>
          {copied ? <Check /> : <Copy />}
        </IconButton>
        {canEdit && messageId && (
          <IconButton label="Edit message" size="xs" onClick={startEdit}>
            <Pencil />
          </IconButton>
        )}
      </div>
    </div>
  )
})
